/**
 * Renders AI-generated Markdown into a constrained set of HTML
 * elements styled to fit the chat / project surfaces.
 *
 * Why a wrapper rather than calling `<ReactMarkdown>` directly: every
 * surface (assistant bubble, project summary card) needs the *same*
 * tag-to-Tailwind mapping. Forking the mapping per call would let the
 * styles drift; centralising it here keeps the look consistent.
 *
 * The backend prompts (see `ai/llm/prompts/ask_v2.md`) constrain the
 * agent to a small Markdown subset: bold / italic / inline code /
 * fenced code / links / bullet lists / paragraphs. Anything outside
 * that list still renders (react-markdown is permissive) but doesn't
 * get bespoke styling.
 */
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

interface Props {
  /** Raw Markdown text. */
  text: string;
  /** Visual density preset; default `"chat"`. */
  variant?: "chat" | "card";
}

/**
 * Tag → Tailwind class map. Same mapping for both variants — the
 * outer `text-[…]` on the parent decides the base size.
 */
const COMPONENTS: Components = {
  // Paragraphs: tight vertical rhythm. We use `mt-1.5` (not `my-*`) so
  // the very first paragraph hugs the bubble's top padding.
  p: ({ children }) => <p className="first:mt-0 mt-2 leading-snug">{children}</p>,
  // Inline emphasis.
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic opacity-90">{children}</em>,
  del: ({ children }) => (
    <del style={{ color: "var(--color-hint)" }}>{children}</del>
  ),
  // Inline code: monospace + faint chip background so it reads as an
  // identifier, not narrative.
  code: ({ children, className }) => {
    // react-markdown calls `code` for BOTH inline code and the inner
    // child of `<pre>` fenced blocks. The `className` carries
    // language-* in the block case; we use its presence to branch.
    const isBlock = typeof className === "string" && className.startsWith("language-");
    if (isBlock) {
      return <code className="font-mono text-[12px]">{children}</code>;
    }
    return (
      <code
        className="font-mono text-[0.9em] px-1 py-0.5 rounded"
        style={{
          background: "color-mix(in srgb, var(--color-text) 8%, transparent)",
        }}
      >
        {children}
      </code>
    );
  },
  // Fenced code block.
  pre: ({ children }) => (
    <pre
      className="font-mono text-[12px] leading-snug whitespace-pre-wrap p-2 rounded-[8px] my-2 overflow-x-auto"
      style={{
        background: "color-mix(in srgb, var(--color-text) 8%, transparent)",
      }}
    >
      {children}
    </pre>
  ),
  // Lists: bulleted only (numbered passes through with default <ol>).
  ul: ({ children }) => (
    <ul className="list-disc pl-4 my-1 space-y-0.5 marker:opacity-60">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-snug">{children}</li>,
  // Headings: we discourage them in prompts but defang anyway.
  h1: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  h2: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  h3: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  h4: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  h5: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  h6: ({ children }) => <p className="font-semibold mt-2">{children}</p>,
  // Links: open in a new tab (target=_blank) so the Mini App stays
  // open while the user reads the source.
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="underline"
      style={{ color: "var(--color-link)" }}
    >
      {children}
    </a>
  ),
  // Defang: don't render images, iframes, raw HTML embed nodes — model
  // shouldn't be sending these per the prompt, but be defensive.
  img: () => null,
};

export function Markdown({ text, variant = "chat" }: Props): React.ReactElement {
  const baseClasses =
    variant === "chat"
      ? "text-[14px] leading-[19px]"
      : "text-[13px] leading-snug";
  return (
    <div className={baseClasses}>
      <ReactMarkdown components={COMPONENTS}>{text}</ReactMarkdown>
    </div>
  );
}
